import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Users, BadgeCheck, MapPin, X } from "lucide-react";
import { LocationPicker } from "../components/LocationPicker";
import type { LocationData } from "../core/types/tsx/components/wizard/ServiceRequestStepLocation.types";

import { Layout } from "../layout/Layout";
import { Sidebar } from "../components/SideBar";
import { ProviderCard } from "../components";
import { useServiceRegistrationWizard } from "../state/service-registration-store";
import { useCategories } from "../hooks/category";
import { useDebounce } from "../hooks/useDebounce";

// Importamos el hook y el tipo oficial
import { useProviders } from "../hooks/control";
import type { ProviderResponse } from "../core/types/control";
import type { ProviderCardProps } from "../core/types/tsx/components/Profesional/ProviderCard.types";

type ProviderAddressLike = {
	name_country?: string | null;
	country?: {
		name_country?: string | null;
	} | null;
	states?: {
		countries?: {
			name_country?: string | null;
		} | null;
		name_state?: string | null;
		name_complete?: string | null;
	} | null;
	name_state?: string | null;
	state?: {
		name_state?: string | null;
	} | null;
	name_city?: string | null;
	city?: {
		name_city?: string | null;
	} | null;
	cities?: {
		name_city?: string | null;
	} | null;
	municipalities?: {
		name_municipality?: string | null;
	} | null;
	parishes?: {
		name_parish?: string | null;
	} | null;
	street_address?: string | null;
	name_address?: string | null;
};

type ProviderSubcategoryLinkLike = {
	subcategories_services?: {
		name_subcategory?: string | null;
	} | null;
};

type ProviderCategoryLinkLike = {
	categories_services?: {
		name_category?: string | null;
	} | null;
	categories_providers?: {
		categories_services?: {
			name_category?: string | null;
		} | null;
	} | null;
	categories_services_providers?:
		| ProviderCategoryLinkLike[]
		| ProviderCategoryLinkLike
		| null;
	subcategories_services_providers?:
		| ProviderSubcategoryLinkLike[]
		| ProviderSubcategoryLinkLike
		| null;
};

type ProviderServiceLike = {
	is_active?: boolean | null;
	name_service?: string | null;
	base_price?: number | string | null;
	categories_services_providers?:
		| ProviderCategoryLinkLike[]
		| ProviderCategoryLinkLike
		| null;
};

type ProviderLike = {
	id?: number | string;
	providerId?: number | string;
	featured?: boolean;
	company_name?: string | null;
	addresses_user?: string | null;
	description_provider?: string | null;
	rating?: number | string | null;
	users?: {
		names?: string | null;
		last_names?: string | null;
		addresses?: ProviderAddressLike[] | null;
		addresses_user?: string | null;
		image_profile?: string | null;
	} | null;
	addresses?: ProviderAddressLike[] | null;
	servicesProviders?: ProviderServiceLike[] | null;
	services_providers?: ProviderServiceLike[] | null;
	services?: ProviderServiceLike[] | null;
	portfolio_metrics?: Array<{
		orders_completed?: number | string | null;
		completed_orders?: number | string | null;
	}> | null;
	orders_completed?: number | string | null;
	completed_orders?: number | string | null;
	ordersCompleted?: number | string | null;
	completedOrders?: number | string | null;
	categories_providers?: Array<{
		categories_services?: {
			name_category?: string | null;
		} | null;
	}> | null;
};

type LocationPriorityQuery = {
	address: string;
	country: string;
	stateName: string;
	city: string;
	street: string;
};

type ProviderLocationSnapshot = {
	country: string;
	stateName: string;
	city: string;
	street: string;
	fullText: string;
};

type ProviderFiltersQuery = {
	page: number;
	pageSize: number;
	name?: string;
	category?: number;
	subcategory?: number;
	rating?: number;
	country?: string;
	state?: string;
	city?: string;
	street?: string;
};

const EMPTY_LOCATION_PRIORITY_QUERY: LocationPriorityQuery = {
	address: "",
	country: "",
	stateName: "",
	city: "",
	street: "",
};

const normalizeLocationText = (value: unknown) => {
	if (typeof value !== "string") return "";
	return value
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/\s+/g, " ")
		.trim();
};

const cleanLocationText = (value: unknown) =>
	typeof value === "string" ? value.trim() : "";

const stripAdministrativePrefix = (value: string) =>
	value
		.replace(
			/^(estado|edo\.?|state|municipio|municipality|parroquia|parish|ciudad|city)\s+/i,
			"",
		)
		.trim();

const looksLikeCoordinates = (value: string) =>
	/^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(value.trim());

const parseAddressByPriority = (rawAddress: string) => {
	const cleanAddress = cleanLocationText(rawAddress);
	if (!cleanAddress || looksLikeCoordinates(cleanAddress)) {
		return { country: "", stateName: "", city: "", street: "" };
	}

	const segments = cleanAddress
		.split(",")
		.map((segment) => segment.trim())
		.filter(Boolean)
		.filter((segment) => !/^\d{3,}$/.test(segment));

	const country = segments.length > 0 ? segments[segments.length - 1] : "";
	const stateName = segments.length > 1 ? segments[segments.length - 2] : "";
	const city = segments.length > 2 ? segments[segments.length - 3] : "";
	const street = segments.length > 0 ? segments[0] : "";

	const dedupe = (value: string, compareTo: string) => {
		if (!value || !compareTo) return value;
		return normalizeLocationText(value) === normalizeLocationText(compareTo)
			? ""
			: value;
	};

	return {
		country,
		stateName: dedupe(stateName, country),
		city: dedupe(city, stateName || country),
		street: dedupe(street, city || stateName || country),
	};
};

const matchesByLocationPriority = (sourceText: string, targetText: string) => {
	const source = normalizeLocationText(sourceText);
	const target = normalizeLocationText(targetText);
	if (!source || !target) return false;
	if (source.includes(target) || target.includes(source)) return true;
	const tokens = target.split(" ").filter((token) => token.length > 2);
	if (tokens.length === 0) return false;
	return tokens.every((token) => source.includes(token));
};

const buildProviderLocationSnapshot = (
	address: ProviderAddressLike,
	fallbackUserAddress: string,
	fallbackProviderAddress: string,
): ProviderLocationSnapshot => {
	const country = cleanLocationText(
		address?.name_country ??
			address?.country?.name_country ??
			address?.states?.countries?.name_country,
	);
	const stateName = cleanLocationText(
		address?.name_state ??
			address?.state?.name_state ??
			address?.states?.name_state ??
			address?.states?.name_complete,
	);
	const city = cleanLocationText(
		address?.name_city ??
			address?.city?.name_city ??
			address?.cities?.name_city ??
			address?.municipalities?.name_municipality ??
			address?.parishes?.name_parish,
	);
	const street = cleanLocationText(
		address?.street_address ??
			address?.name_address ??
			fallbackUserAddress ??
			fallbackProviderAddress,
	);

	const fullText = [
		street,
		city,
		stateName,
		country,
		fallbackUserAddress,
		fallbackProviderAddress,
	]
		.filter(Boolean)
		.join(", ");

	return { country, stateName, city, street, fullText };
};

const extractProviderLocationSnapshots = (
	provider: ProviderResponse,
): ProviderLocationSnapshot[] => {
	const providerRecord = provider as ProviderLike;
	const userRecord = providerRecord?.users ?? {};
	const addresses = Array.isArray(userRecord?.addresses)
		? userRecord.addresses
		: Array.isArray(providerRecord?.addresses)
			? providerRecord.addresses
			: [];

	const fallbackUserAddress = cleanLocationText(userRecord?.addresses_user);
	const fallbackProviderAddress = cleanLocationText(providerRecord?.addresses_user);

	const snapshots = addresses
		.map((address) =>
			buildProviderLocationSnapshot(
				address,
				fallbackUserAddress,
				fallbackProviderAddress,
			),
		)
		.filter((snapshot) =>
			Boolean(
				snapshot.fullText ||
				snapshot.country ||
				snapshot.stateName ||
				snapshot.city ||
				snapshot.street,
			),
		);

	if (snapshots.length > 0) {
		return snapshots;
	}

	if (fallbackUserAddress || fallbackProviderAddress) {
		return [
			buildProviderLocationSnapshot(
				{},
				fallbackUserAddress,
				fallbackProviderAddress,
			),
		];
	}

	return [];
};

const hasLocationPriorityQuery = (query: LocationPriorityQuery) =>
	Boolean(
		query.address || query.country || query.stateName || query.city || query.street,
	);

const buildLocationPriorityScore = (
	provider: ProviderResponse,
	query: LocationPriorityQuery,
) => {
	const snapshots = extractProviderLocationSnapshots(provider);
	if (snapshots.length === 0) return 0;

	let bestScore = 0;

	for (const snapshot of snapshots) {
		const stateMatch = query.stateName
			? matchesByLocationPriority(
					snapshot.stateName || snapshot.fullText,
					query.stateName,
				)
			: false;
		const countryMatch = query.country
			? matchesByLocationPriority(
					snapshot.country || snapshot.fullText,
					query.country,
				)
			: false;
		const cityMatch = query.city
			? matchesByLocationPriority(snapshot.city || snapshot.fullText, query.city)
			: false;
		const streetMatch = query.street
			? matchesByLocationPriority(
					snapshot.street || snapshot.fullText,
					query.street,
				)
			: false;

		let score = 0;
		if (countryMatch && stateMatch) score += 500;
		else if (stateMatch) score += 420;
		else if (countryMatch) score += 320;

		if (cityMatch) score += 180;
		if (streetMatch) score += 90;

		if (
			query.address &&
			matchesByLocationPriority(snapshot.fullText, query.address)
		) {
			score += 40;
		}

		if (score > bestScore) {
			bestScore = score;
		}
	}

	return bestScore;
};

const hasStreetMatchAcrossSavedAddresses = (
	provider: ProviderResponse,
	street: string,
) => {
	const normalizedStreet = cleanLocationText(street);
	if (!normalizedStreet) return false;

	const snapshots = extractProviderLocationSnapshots(provider);
	return snapshots.some((snapshot) =>
		matchesByLocationPriority(snapshot.street || snapshot.fullText, normalizedStreet),
	);
};

const getActiveProviderServices = (provider: ProviderResponse) => {
	const services = Array.isArray(provider.services_providers)
		? (provider.services_providers as ProviderServiceLike[])
		: [];

	return services.filter((service) => service?.is_active !== false);
};

export function ProveedoresGeneral() {
	const location = useLocation() as {
		search: string;
		state?: {
			fromWizard?: boolean;
		};
	};
	const { serviceRequestData } = useServiceRegistrationWizard();
	const hasHydratedFromEntryRef = useRef(false);

	// =========================================================================
	// ESTADOS DE FILTROS
	// =========================================================================
	const [selectedCategory, setSelectedCategory] = useState<string>("Todos");
	const [selectedPriceRange, setSelectedPriceRange] = useState<{
		min: number;
		max: number;
	}>({ min: 0, max: 0 });
	const [activePriceThumb, setActivePriceThumb] = useState<"min" | "max" | null>(null);

	const [filterBestRated, setFilterBestRated] = useState(false);
	const [search, setSearch] = useState("");
	const [subcategoryFilter, setSubcategoryFilter] = useState<string[]>([]);
	const [locationPriorityQuery, setLocationPriorityQuery] =
		useState<LocationPriorityQuery>(EMPTY_LOCATION_PRIORITY_QUERY);
	const { data: categoriesData } = useCategories();
	const debouncedSearch = useDebounce(search, 350);
	const debouncedSelectedCategory = useDebounce(selectedCategory, 300);
	const debouncedSubcategoryFilter = useDebounce(subcategoryFilter, 300);
	const debouncedLocationPriorityQuery = useDebounce(locationPriorityQuery, 450);
	const debouncedSelectedPriceRange = useDebounce(selectedPriceRange, 350);
	const debouncedFilterBestRated = useDebounce(filterBestRated, 250);
	const [showLocationModal, setShowLocationModal] = useState(false);
	const [locationModalValue, setLocationModalValue] = useState<LocationData>({
		nombreDireccion: "",
		direccion: "",
		referencias: "",
	});
	const subcategoryOptions = useMemo(() => {
		if (!Array.isArray(categoriesData) || categoriesData.length === 0) return [];

		if (selectedCategory === "Todos") {
			return categoriesData.flatMap((category) =>
				Array.isArray(category.subcategories_services)
					? category.subcategories_services.map((subcategory) => ({
							id: subcategory.id,
							name: subcategory.name_subcategory,
						}))
					: [],
			);
		}

		const selected = categoriesData.find(
			(category) =>
				category.name_category.toLowerCase() === selectedCategory.toLowerCase(),
		);

		if (!selected || !Array.isArray(selected.subcategories_services)) return [];

		return selected.subcategories_services.map((subcategory) => ({
			id: subcategory.id,
			name: subcategory.name_subcategory,
		}));
	}, [categoriesData, selectedCategory]);

	const popularProviderIds = [1, 2, 3];

	const getServiceCategoryName = useCallback((service: ProviderServiceLike): string => {
		const rawLinks = Array.isArray(service?.categories_services_providers)
			? service.categories_services_providers
			: service?.categories_services_providers
				? [service.categories_services_providers]
				: [];

		for (const link of rawLinks) {
			const categoryName =
				link?.categories_providers?.categories_services?.name_category ??
				link?.categories_services?.name_category ??
				"";
			if (typeof categoryName === "string" && categoryName.trim()) {
				return categoryName;
			}
		}

		return "";
	}, []);

	const resolveProviderPricesByCategory = useCallback(
		(provider: ProviderResponse, category: string): number[] => {
			const services = getActiveProviderServices(provider);
			const normalizedCategory = category.toLowerCase();

			const matched = services
				.filter((service) => {
					if (category === "Todos") return true;
					const categoryName = getServiceCategoryName(service).toLowerCase();
					return categoryName === normalizedCategory;
				})
				.map((service) => Number(service?.base_price))
				.filter((price: number) => Number.isFinite(price) && price > 0);

			if (matched.length > 0) return matched;

			return services
				.map((service) => Number(service?.base_price))
				.filter((price: number) => Number.isFinite(price) && price > 0);
		},
		[getServiceCategoryName],
	);

	const selectedCategoryId = useMemo(() => {
		if (debouncedSelectedCategory === "Todos") return undefined;
		const found = categoriesData?.find(
			(category) =>
				category.name_category.toLowerCase() ===
				debouncedSelectedCategory.toLowerCase(),
		);
		return typeof found?.id === "number" ? found.id : undefined;
	}, [categoriesData, debouncedSelectedCategory]);

	const selectedSubcategoryId = useMemo(() => {
		if (debouncedSubcategoryFilter.length === 0) return undefined;

		const found = categoriesData
			?.flatMap((category) =>
				Array.isArray(category.subcategories_services)
					? category.subcategories_services
					: [],
			)
			.find(
				(subcategory) =>
					subcategory.name_subcategory.toLowerCase() ===
					debouncedSubcategoryFilter[0]?.toLowerCase(),
			);

		return typeof found?.id === "number" ? found.id : undefined;
	}, [categoriesData, debouncedSubcategoryFilter]);

	const backendLocationQuery = useMemo(
		() => ({
			country: debouncedLocationPriorityQuery.country || undefined,
			state:
				stripAdministrativePrefix(debouncedLocationPriorityQuery.stateName) ||
				undefined,
			city:
				stripAdministrativePrefix(debouncedLocationPriorityQuery.city) ||
				undefined,
			street:
				stripAdministrativePrefix(debouncedLocationPriorityQuery.street) ||
				undefined,
		}),
		[
			debouncedLocationPriorityQuery.country,
			debouncedLocationPriorityQuery.stateName,
			debouncedLocationPriorityQuery.city,
			debouncedLocationPriorityQuery.street,
		],
	);

	// =========================================================================
	// HOOK DE PROVEEDORES
	// =========================================================================
	const providerFilters = useMemo<ProviderFiltersQuery>(
		() => ({
			page: 1,
			pageSize: 200,
			name: debouncedSearch.trim() || undefined,
			category: selectedCategoryId,
			subcategory: selectedSubcategoryId,
			country: backendLocationQuery.country,
			state: backendLocationQuery.state,
			city: backendLocationQuery.city,
			street: backendLocationQuery.street,
			rating: debouncedFilterBestRated ? 4 : undefined,
		}),
		[
			debouncedSearch,
			selectedCategoryId,
			selectedSubcategoryId,
			backendLocationQuery,
			debouncedFilterBestRated,
		],
	);
	const providerFiltersKey = useMemo(
		() => JSON.stringify(providerFilters),
		[providerFilters],
	);
	const locationQueryFallbackRef = useRef<string>("");

	const handleCategorySelect = (category: string) => {
		setSelectedCategory(category);
		setSubcategoryFilter((prev) => {
			if (prev.length === 0 || category === "Todos") return prev;

			const categoryData = categoriesData?.find(
				(item) => item.name_category.toLowerCase() === category.toLowerCase(),
			);
			const categorySubNames = new Set(
				(categoryData?.subcategories_services ?? []).map((subcategory) =>
					subcategory.name_subcategory.toLowerCase(),
				),
			);

			return prev.filter((selectedSubcategory) =>
				categorySubNames.has(selectedSubcategory.toLowerCase()),
			);
		});
	};

	const handleClearAllFilters = () => {
		setSelectedCategory("Todos");
		setSubcategoryFilter([]);
		setSelectedPriceRange({ min: priceBounds.min, max: priceBounds.max });
		setFilterBestRated(false);
		setSearch("");
		setLocationPriorityQuery({
			address: "",
			country: "",
			stateName: "",
			city: "",
			street: "",
		});
	};

	// Mantenemos auto en FALSE para tener control absoluto.
	const providersApi = useProviders(providerFilters, false);

	const normalizeProviderList = (payload: unknown): ProviderResponse[] => {
		if (Array.isArray(payload)) return payload as ProviderResponse[];
		if (!payload || typeof payload !== "object") return [];

		const record = payload as {
			items?: unknown[];
			providers?: unknown[];
			data?: unknown[];
		};

		if (Array.isArray(record.providers))
			return record.providers as ProviderResponse[];
		if (Array.isArray(record.data)) return record.data as ProviderResponse[];
		if (Array.isArray(record.items)) return record.items as ProviderResponse[];
		return [];
	};

	// Keep normalized data declared before effects that consume it.
	const rawProviders: ProviderResponse[] = normalizeProviderList(providersApi.data);

	const buildLocationFallbackQueries = (
		baseQuery: ProviderFiltersQuery,
	): ProviderFiltersQuery[] => {
		const queries: ProviderFiltersQuery[] = [];
		const seen = new Set<string>();

		const pushUnique = (query: ProviderFiltersQuery) => {
			const key = JSON.stringify(query);
			if (key === providerFiltersKey || seen.has(key)) return;
			seen.add(key);
			queries.push(query);
		};

		if (baseQuery.street) {
			pushUnique({ ...baseQuery, street: undefined });
		}

		if (baseQuery.city || baseQuery.street) {
			pushUnique({ ...baseQuery, city: undefined, street: undefined });
		}

		if (baseQuery.state && baseQuery.city) {
			pushUnique({
				...baseQuery,
				state: baseQuery.city,
				city: baseQuery.state,
				street: undefined,
			});
		}

		if (baseQuery.country && baseQuery.state) {
			pushUnique({
				...baseQuery,
				country: undefined,
				city: undefined,
				street: undefined,
			});
		}

		if (!baseQuery.state && baseQuery.city) {
			pushUnique({
				...baseQuery,
				state: baseQuery.city,
				city: undefined,
				street: undefined,
				country: undefined,
			});
		}

		return queries;
	};

	// LA SOLUCIÓN AL 429: Control manual de la petición.
	// Solo se ejecutará una vez cuando providerFilters cambie realmente.
	useEffect(() => {
		locationQueryFallbackRef.current = "";
		void providersApi.execute();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [providerFiltersKey]);

	useEffect(() => {
		if (providersApi.loading) return;

		const hasApiLocationFilter = Boolean(
			providerFilters.country ||
			providerFilters.state ||
			providerFilters.city ||
			providerFilters.street,
		);

		if (!hasApiLocationFilter) return;

		const hasAnyLocationMatch = rawProviders.some(
			(provider) => buildLocationPriorityScore(provider, locationPriorityQuery) > 0,
		);
		if (hasAnyLocationMatch) return;

		if (locationQueryFallbackRef.current === providerFiltersKey) return;
		locationQueryFallbackRef.current = providerFiltersKey;

		const fallbackQueries = buildLocationFallbackQueries(providerFilters);
		if (fallbackQueries.length === 0) return;

		const runFallback = async () => {
			for (const fallbackQuery of fallbackQueries) {
				try {
					const payload = await providersApi.execute({ query: fallbackQuery });
					const providers = normalizeProviderList(payload);
					if (providers.length > 0) {
						return;
					}
				} catch {
					// Try next relaxed location query.
				}
			}
		};

		void runFallback();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [providersApi.loading, providerFiltersKey, rawProviders, locationPriorityQuery]);

	// =========================================================================
	// EFECTOS (Leer parámetros de URL)
	// =========================================================================
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const hasParams = params.toString().trim().length > 0;
		const fromWizard = Boolean(location.state?.fromWizard);

		if (hasParams) {
			const category = params.get("category") || "Todos";
			const addressParam = cleanLocationText(params.get("address"));
			const countryParam = cleanLocationText(params.get("country"));
			const stateNameParam = cleanLocationText(params.get("stateName"));
			const cityParam = cleanLocationText(params.get("city"));
			const streetParam = cleanLocationText(params.get("street"));
			const subcategoryParam = params.get("subcategory");
			const searchParam = params.get("search");

			setSelectedCategory(category);
			setSubcategoryFilter(
				subcategoryParam ? subcategoryParam.split(",").filter(Boolean) : [],
			);
			setSearch(searchParam ? searchParam : "");

			const parsedAddress = parseAddressByPriority(addressParam);
			setLocationPriorityQuery({
				address: addressParam,
				country: countryParam || parsedAddress.country,
				stateName: stateNameParam || parsedAddress.stateName,
				city: cityParam || parsedAddress.city,
				street: streetParam || parsedAddress.street,
			});
			hasHydratedFromEntryRef.current = true;
			return;
		}

		if (hasHydratedFromEntryRef.current) return;
		if (!fromWizard) {
			hasHydratedFromEntryRef.current = true;
			return;
		}

		const contextCategory =
			typeof serviceRequestData.category === "string" &&
			serviceRequestData.category.trim().length > 0
				? serviceRequestData.category.trim()
				: "Todos";
		const contextSubcategories = Array.isArray(serviceRequestData.subcategory)
			? serviceRequestData.subcategory.filter(Boolean)
			: [];
		const contextAddress = cleanLocationText(serviceRequestData.location?.direccion);

		const parsedAddress = parseAddressByPriority(contextAddress);

		setSelectedCategory(contextCategory);
		setSubcategoryFilter(contextSubcategories);
		setSearch("");
		setLocationPriorityQuery({
			address: contextAddress,
			country: parsedAddress.country,
			stateName: parsedAddress.stateName,
			city: parsedAddress.city,
			street: parsedAddress.street,
		});
		hasHydratedFromEntryRef.current = true;
	}, [location.search, location.state?.fromWizard, serviceRequestData]);

	// =========================================================================
	// FILTRADO LOCAL DE DATOS (INSTANTÁNEO)
	// =========================================================================

	// SOLUCIÓN AL COMPILADOR DE REACT:
	// Usamos la data normalizada para que el arreglo de dependencias sea plano y directo.
	const priceBounds = useMemo(() => {
		const prices = rawProviders.flatMap((provider) =>
			resolveProviderPricesByCategory(provider, selectedCategory),
		);

		if (prices.length === 0) return { min: 0, max: 100 };

		return {
			min: Math.floor(Math.min(...prices)),
			max: Math.ceil(Math.max(...prices)),
		};
	}, [rawProviders, selectedCategory, resolveProviderPricesByCategory]);

	useEffect(() => {
		setSelectedPriceRange({ min: priceBounds.min, max: priceBounds.max });
	}, [priceBounds.min, priceBounds.max]);

	const locationAwareProviders = useMemo(() => {
		let filtered: ProviderResponse[] = [...rawProviders];
		let streetFallbackToArea = false;

		// Búsqueda de texto
		if (search.trim() !== "") {
			const s = search.trim().toLowerCase();
			filtered = filtered.filter(
				(p) =>
					(p.company_name && p.company_name.toLowerCase().includes(s)) ||
					(p.description_provider &&
						p.description_provider.toLowerCase().includes(s)) ||
					(p.users &&
						(p.users.names + " " + p.users.last_names)
							.toLowerCase()
							.includes(s)),
			);
		}

		// Filtro por subcategorías del wizard
		if (subcategoryFilter.length > 0) {
			const lowerSubs = subcategoryFilter.map((s) => s.toLowerCase());
			filtered = filtered.filter((p) => {
				const providerRecord = p as ProviderLike;

				// 1) Check services_providers for matching service names or subcategory names
				const services = getActiveProviderServices(p);
				const matchInServices = services.some((srv) => {
					// Match by service name
					const serviceName =
						typeof srv.name_service === "string" ? srv.name_service : "";
					if (
						serviceName &&
						lowerSubs.some((sub) => serviceName.toLowerCase().includes(sub))
					)
						return true;

					// categories_services_providers can be an object (unique) or array
					const catLinks = Array.isArray(srv.categories_services_providers)
						? srv.categories_services_providers
						: srv.categories_services_providers
							? [srv.categories_services_providers]
							: [];

					return catLinks.some((link) => {
						const subLinks = Array.isArray(
							link.subcategories_services_providers,
						)
							? link.subcategories_services_providers
							: [];
						return subLinks.some((subLink) => {
							const subName =
								subLink?.subcategories_services?.name_subcategory ?? "";
							return lowerSubs.some(
								(sub) =>
									typeof subName === "string" &&
									subName.toLowerCase().includes(sub),
							);
						});
					});
				});
				if (matchInServices) return true;

				// 2) Fallback: check top-level categories_providers → categories_services.name_category
				//    This catches cases where the subcategory name matches a category name
				const catProviders = Array.isArray(providerRecord.categories_providers)
					? providerRecord.categories_providers
					: [];
				return catProviders.some((cp) => {
					const catName = cp?.categories_services?.name_category ?? "";
					return (
						typeof catName === "string" &&
						lowerSubs.some((sub) => catName.toLowerCase().includes(sub))
					);
				});
			});
		}

		filtered = filtered.filter((provider) => {
			const prices = resolveProviderPricesByCategory(provider, selectedCategory);
			if (prices.length === 0) return true;
			const minProviderPrice = Math.min(...prices);
			return (
				minProviderPrice >= debouncedSelectedPriceRange.min &&
				minProviderPrice <= debouncedSelectedPriceRange.max
			);
		});

		const shouldSortByPriorityLocation =
			hasLocationPriorityQuery(locationPriorityQuery);
		let scoringQuery: LocationPriorityQuery = locationPriorityQuery;

		/* ---- Location-priority scoring, filtering & fallback ---- */
		if (shouldSortByPriorityLocation || filterBestRated) {
			const priorityScoreCache = new Map<string, number>();
			const getPriorityScore = (
				provider: ProviderResponse,
				query: LocationPriorityQuery,
			) => {
				const cacheKey = `${provider.id}|${query.country}|${query.stateName}|${query.city}|${query.street}|${query.address}`;
				const cached = priorityScoreCache.get(cacheKey);
				if (typeof cached === "number") return cached;
				const score = buildLocationPriorityScore(provider, query);
				priorityScoreCache.set(cacheKey, score);
				return score;
			};

			/*
			 * When the wizard provided a location query, only keep providers
			 * that match at least one location level (score > 0).
			 * If nobody matches, fall back to showing everyone.
			 */
			if (shouldSortByPriorityLocation) {
				let withScore = filtered.filter(
					(p) => getPriorityScore(p, scoringQuery) > 0,
				);

				if (
					withScore.length === 0 &&
					Boolean(locationPriorityQuery.street) &&
					Boolean(
						locationPriorityQuery.city ||
						locationPriorityQuery.stateName ||
						locationPriorityQuery.country,
					)
				) {
					const fallbackQuery: LocationPriorityQuery = {
						...locationPriorityQuery,
						street: "",
					};
					const withAreaScore = filtered.filter(
						(p) => getPriorityScore(p, fallbackQuery) > 0,
					);

					if (withAreaScore.length > 0) {
						withScore = withAreaScore;
						scoringQuery = fallbackQuery;
						streetFallbackToArea = true;
					}
				}

				if (withScore.length > 0) {
					filtered = withScore;
				}
			}

			filtered = filtered.sort((a, b) => {
				if (shouldSortByPriorityLocation) {
					const scoreDiff =
						getPriorityScore(b, scoringQuery) -
						getPriorityScore(a, scoringQuery);
					if (scoreDiff !== 0) return scoreDiff;
				}

				if (filterBestRated) {
					const ratingDiff = (b.rating ?? 0) - (a.rating ?? 0);
					if (ratingDiff !== 0) return ratingDiff;
				}

				return 0;
			});
		}

		return {
			providers: filtered,
			streetFallbackToArea,
		};
	}, [
		rawProviders,
		search,
		selectedCategory,
		subcategoryFilter,
		filterBestRated,
		debouncedSelectedPriceRange.min,
		debouncedSelectedPriceRange.max,
		locationPriorityQuery,
		resolveProviderPricesByCategory,
	]);

	const filteredProviders = locationAwareProviders.providers;

	/* ---- Location fallback message ---- */
	const locationFallbackMessage = useMemo(() => {
		if (!hasLocationPriorityQuery(locationPriorityQuery)) return null;
		if (filteredProviders.length === 0) return null;

		if (locationAwareProviders.streetFallbackToArea) {
			const label = [locationPriorityQuery.city, locationPriorityQuery.stateName]
				.filter(Boolean)
				.join(", ");
			if (!label) {
				return "No se encontraron proveedores en tu calle. Mostrando proveedores por ciudad o estado.";
			}

			return `No se encontraron proveedores en tu calle. Mostrando proveedores en ${label}.`;
		}

		const hasStreet = Boolean(locationPriorityQuery.street);
		if (!hasStreet) return null;
		const someMatchStreet = filteredProviders.some((provider) =>
			hasStreetMatchAcrossSavedAddresses(provider, locationPriorityQuery.street),
		);

		if (someMatchStreet) return null;

		const label = [locationPriorityQuery.city, locationPriorityQuery.stateName]
			.filter(Boolean)
			.join(", ");
		if (!label) return null;

		return `No se encontraron proveedores en tu calle. Mostrando proveedores en ${label}.`;
	}, [
		filteredProviders,
		locationPriorityQuery,
		locationAwareProviders.streetFallbackToArea,
	]);

	const priceRangeSpan = Math.max(priceBounds.max - priceBounds.min, 1);
	const priceMinPercent =
		((selectedPriceRange.min - priceBounds.min) / priceRangeSpan) * 100;
	const priceMaxPercent =
		((selectedPriceRange.max - priceBounds.min) / priceRangeSpan) * 100;

	// =========================================================================
	// RENDERIZADO
	// =========================================================================
	return (
		<Layout>
			<div className='flex flex-1 bg-slate-50'>
				<Sidebar
					selectedCategory={selectedCategory}
					onSelect={handleCategorySelect}
				/>

				<main className='flex-1 min-w-0 px-4 py-6 md:px-6 lg:px-8'>
					<div className='mx-auto w-full max-w-7xl space-y-4'>
						<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
							<div className='relative flex-1'>
								<input
									type='text'
									placeholder='¿Qué servicio estas buscando?'
									aria-label='Buscar servicio'
									className='w-full rounded-full border border-border bg-[#fcfcfc] px-4 py-3 text-sm text-slate-700 shadow-xs outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
									className='absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-primary cursor-pointer'
									aria-hidden='true'
								>
									<circle cx='11' cy='11' r='8' />
									<line x1='21' y1='21' x2='16.65' y2='16.65' />
								</svg>
							</div>
						</div>

						<div className='grid grid-cols-1 gap-2 sm:flex sm:flex-wrap'>
							<select
								value={subcategoryFilter[0] ?? ""}
								onChange={(event) =>
									setSubcategoryFilter(
										event.target.value ? [event.target.value] : [],
									)
								}
								className='w-full rounded-full border border-border bg-[#fcfcfc] px-3 py-2 text-sm font-semibold text-slate-700 shadow-xs sm:w-auto'
							>
								<option value=''>Subcategoría: todas</option>
								{subcategoryOptions.map((subcategory) => (
									<option key={subcategory.id} value={subcategory.name}>
										{subcategory.name}
									</option>
								))}
							</select>
							<button
								type='button'
								onClick={() => {
									setLocationModalValue({
										nombreDireccion: "",
										direccion: locationPriorityQuery.address || "",
										referencias: "",
									});
									setShowLocationModal(true);
								}}
								className={`inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold shadow-xs transition hover:bg-primary hover:text-white sm:w-auto ${hasLocationPriorityQuery(locationPriorityQuery) ? "border-primary bg-primary/10 text-primary" : "border-border bg-[#fcfcfc] text-slate-700"}`}
							>
								<MapPin className='h-4 w-4' />
								{hasLocationPriorityQuery(locationPriorityQuery)
									? locationPriorityQuery.city ||
										locationPriorityQuery.stateName ||
										locationPriorityQuery.address ||
										"Ubicación activa"
									: "Filtrar por ubicación"}
							</button>
							{hasLocationPriorityQuery(locationPriorityQuery) && (
								<button
									type='button'
									onClick={() =>
										setLocationPriorityQuery(
											EMPTY_LOCATION_PRIORITY_QUERY,
										)
									}
									aria-label='Limpiar ubicación'
									className='inline-flex w-full cursor-pointer items-center justify-center rounded-full border border-border bg-[#fcfcfc] p-2 text-slate-500 shadow-xs transition hover:bg-red-50 hover:text-red-600 sm:w-auto'
								>
									<X className='h-4 w-4' />
								</button>
							)}
							<div className='flex w-full items-center gap-2 rounded-full border border-border bg-[#fcfcfc] px-3 py-2 text-sm font-semibold text-slate-700 shadow-xs sm:w-auto'>
								<span className='min-w-10 text-right'>
									${selectedPriceRange.min}
								</span>
								<div className='relative h-7 flex-1 sm:w-40'>
									<div className='absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-secondary' />
									<div
										className='absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-primary'
										style={{
											left: `${priceMinPercent}%`,
											width: `${Math.max(priceMaxPercent - priceMinPercent, 0)}%`,
										}}
									/>
									{activePriceThumb === "min" ? (
										<div
											className='absolute -top-7 -translate-x-1/2 rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-white shadow-sm'
											style={{ left: `${priceMinPercent}%` }}
										>
											${selectedPriceRange.min}
										</div>
									) : null}
									{activePriceThumb === "max" ? (
										<div
											className='absolute -top-7 -translate-x-1/2 rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-white shadow-sm'
											style={{ left: `${priceMaxPercent}%` }}
										>
											${selectedPriceRange.max}
										</div>
									) : null}
									<input
										type='range'
										min={priceBounds.min}
										max={priceBounds.max}
										value={selectedPriceRange.min}
										onMouseDown={() => setActivePriceThumb("min")}
										onTouchStart={() => setActivePriceThumb("min")}
										onFocus={() => setActivePriceThumb("min")}
										onMouseUp={() => setActivePriceThumb(null)}
										onTouchEnd={() => setActivePriceThumb(null)}
										onBlur={() => setActivePriceThumb(null)}
										onChange={(event) => {
											const nextMin = Number(event.target.value);
											setSelectedPriceRange((prev) => ({
												min: Math.min(nextMin, prev.max),
												max: prev.max,
											}));
										}}
										className='pointer-events-none absolute inset-0 w-full appearance-none bg-transparent accent-primary [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary'
									/>
									<input
										type='range'
										min={priceBounds.min}
										max={priceBounds.max}
										value={selectedPriceRange.max}
										onMouseDown={() => setActivePriceThumb("max")}
										onTouchStart={() => setActivePriceThumb("max")}
										onFocus={() => setActivePriceThumb("max")}
										onMouseUp={() => setActivePriceThumb(null)}
										onTouchEnd={() => setActivePriceThumb(null)}
										onBlur={() => setActivePriceThumb(null)}
										onChange={(event) => {
											const nextMax = Number(event.target.value);
											setSelectedPriceRange((prev) => ({
												min: prev.min,
												max: Math.max(nextMax, prev.min),
											}));
										}}
										className='pointer-events-none absolute inset-0 w-full appearance-none bg-transparent accent-primary [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary'
									/>
								</div>
								<span className='min-w-10'>
									${selectedPriceRange.max}
								</span>
							</div>
							<button
								type='button'
								className='cursor-pointer rounded-full border border-border bg-[#fcfcfc] px-3 py-2 text-sm font-semibold text-slate-700 shadow-xs transition hover:bg-primary hover:text-white'
								onClick={handleClearAllFilters}
							>
								Limpiar filtros
							</button>
							<button
								type='button'
								className={`cursor-pointer rounded-full border border-border px-3 py-2 text-sm font-semibold shadow-xs transition hover:bg-primary hover:text-white ${filterBestRated ? "bg-primary text-white" : "bg-[#fcfcfc] text-slate-700"}`}
								onClick={() => setFilterBestRated((prev) => !prev)}
							>
								Mejor valorados
							</button>
						</div>
					</div>

					<div className='mx-auto mt-10 w-full max-w-7xl sm:mt-14'>
						{locationFallbackMessage ? (
							<div className='mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
								{locationFallbackMessage}
							</div>
						) : null}

						{providersApi.loading && (
							<p className='text-center font-semibold text-primary py-4'>
								Buscando proveedores...
							</p>
						)}

						{!providersApi.loading && (
							<ProviderCard
								selectedCategory={selectedCategory}
								selectedRanges={[]}
								selectedAvailability={[]}
								featuredIds={popularProviderIds}
								// SOLUCIÓN TYPESCRIPT: Pasamos la lista con un casteo explícito porque ProviderCard la normaliza internamente.
								providers={
									filteredProviders as unknown as ProviderCardProps["providers"]
								}
								// Pasamos explicitamente undefined para que la Card jamás intente hacer una petición por sí sola.
								fetchProviders={undefined}
							/>
						)}
					</div>

					<section className='mt-10 px-0 py-6 sm:px-4'>
						<h1 className='mb-2 text-center text-2xl font-bold text-slate-900'>
							¿Por Qué Elegir <span className='text-primary'>Servix?</span>
						</h1>
						<p className='mb-6 text-center text-slate-600'>
							La mejor plataforma para encontrar ayuda profesional
						</p>
						<div className='mx-auto grid w-full max-w-7xl gap-4 sm:grid-cols-2 xl:grid-cols-3 sm:gap-5'>
							{[
								{
									title: "Profesionales Verificados",
									text: "Todos los profesionales son rigurosamente examinados y verificados para tu tranquilidad",
									Icon: ShieldCheck,
								},
								{
									title: "Comunidad de Confianza",
									text: "Únete a más de 1 millón de usuarios que han completado con éxito sus proyectos",
									Icon: Users,
								},
								{
									title: "Calidad Garantizada",
									text: "Garantía de satisfacción del 100% con pagos seguros y resolución de disputas",
									Icon: BadgeCheck,
								},
							].map((item) => (
								<div
									key={item.title}
									className='flex min-w-0 flex-1 flex-col justify-center rounded-xl border border-slate-200 bg-[#fcfcfc] p-5 text-center shadow-md min-h-52 sm:p-6 sm:min-h-60'
								>
									<div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-primary'>
										<item.Icon
											className='h-6 w-6'
											strokeWidth={2.5}
										/>
									</div>
									<h2 className='mb-1 text-lg font-semibold text-slate-900'>
										{item.title}
									</h2>
									<p className='text-sm text-slate-600'>{item.text}</p>
								</div>
							))}
						</div>
					</section>
				</main>
			</div>

			{showLocationModal && (
				<div
					className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'
					onClick={(e) => {
						if (e.target === e.currentTarget) setShowLocationModal(false);
					}}
				>
					<div className='relative flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'>
						<LocationPicker
							value={locationModalValue}
							onChange={setLocationModalValue}
							onClose={() => setShowLocationModal(false)}
							onConfirm={() => {
								const parsed = parseAddressByPriority(
									locationModalValue.direccion,
								);
								setLocationPriorityQuery({
									address: locationModalValue.direccion,
									country: parsed.country,
									stateName: parsed.stateName,
									city: parsed.city,
									street: parsed.street,
								});
								setShowLocationModal(false);
							}}
						/>
					</div>
				</div>
			)}
		</Layout>
	);
}
